import { db, storage } from '../firebaseConfig';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

// Client-side mirror of storage.rules' write-time checks, so a bad file is
// rejected instantly instead of round-tripping to Storage first.
export const MAX_PROFILE_PICTURE_BYTES = 5 * 1024 * 1024;

// The Storage SDK's own retry logic can leave a broken upload (e.g. an
// unreachable bucket) hanging far longer than a user will wait, with the UI
// stuck on "Saving..." and no error to show. Bounding it here guarantees
// updateProfilePicture always settles, so the caller's try/catch can turn a
// stuck upload into a normal friendly error like any other failure.
const withTimeout = (promise, ms, message) =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(message)), ms))
  ]);

/**
 * Service to handle Coach-Centric sub-profile persistence.
 *
 * `isActive` denotes enabled / not-archived (NOT "currently selected" —
 * selection lives in client state). The 2-profile free-tier cap is enforced
 * client-side here only; there is no server-side counter/Cloud Function yet.
 */

// Free-tier limit: a Manager may hold up to 2 non-archived profiles.
// Exported so UI (e.g. the profile switcher's "N / 2 Heroes" cap indicator)
// reads the same number the create guard enforces, rather than hardcoding it.
export const FREE_PROFILE_LIMIT = 2;

/**
 * Create a new sub-profile for the given owner.
 * Rejects if the owner is already at the free-tier cap of non-archived profiles.
 * Returns { success: true, id } on success.
 */
export const createProfile = async (ownerUserId, name) => {
  try {
    // Count existing non-archived profiles for this owner.
    const snapshot = await getDocs(
      query(collection(db, "profiles"), where("ownerUserId", "==", ownerUserId))
    );
    const activeCount = snapshot.docs.filter(
      (d) => d.data().isActive !== false
    ).length;

    if (activeCount >= FREE_PROFILE_LIMIT) {
      console.warn("Profile cap reached: ", ownerUserId);
      return {
        success: false,
        error: `Free-tier limit reached (${FREE_PROFILE_LIMIT} profiles). Additional Heroes coming soon.`,
        limitReached: true
      };
    }

    const docRef = await addDoc(collection(db, "profiles"), {
      ownerUserId,
      name,
      isActive: true,
      createdAt: serverTimestamp()
    });

    console.log("Profile Recruited: ", docRef.id);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("Transmission Error: ", error);
    return { success: false, error };
  }
};

/**
 * List all profiles for an owner (archived included — caller filters for display),
 * sorted by createdAt ascending (oldest first) for default-profile fallback.
 * Sorted client-side to avoid a composite index requirement.
 */
export const listProfiles = async (ownerUserId) => {
  try {
    const snapshot = await getDocs(
      query(collection(db, "profiles"), where("ownerUserId", "==", ownerUserId))
    );

    const profiles = snapshot.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => {
        // serverTimestamp may be null momentarily for a just-written doc;
        // treat missing timestamps as newest so they sort last.
        const aTime = a.createdAt?.toMillis?.() ?? Infinity;
        const bTime = b.createdAt?.toMillis?.() ?? Infinity;
        return aTime - bTime;
      });

    return { success: true, profiles };
  } catch (error) {
    console.error("Transmission Error: ", error);
    return { success: false, error };
  }
};

/**
 * Rename an existing profile.
 */
export const renameProfile = async (profileId, newName) => {
  try {
    await updateDoc(doc(db, "profiles", profileId), { name: newName });
    console.log("Profile Renamed: ", profileId);
    return { success: true, id: profileId };
  } catch (error) {
    console.error("Transmission Error: ", error);
    return { success: false, error };
  }
};

/**
 * Upload a new profile picture and point the profile doc at it.
 * Stores each upload under a timestamped filename (never overwrites the
 * previous object in place) so the returned download URL is always fresh --
 * an in-place overwrite would keep the old URL's cached image showing in
 * some browsers even after the file content changed. `previousPhotoPath`
 * (the profile doc's current `photoPath`, if any) is best-effort deleted
 * after the new upload succeeds, so old pictures don't pile up in Storage;
 * that cleanup failing (e.g. already gone) never blocks the new photo.
 */
export const updateProfilePicture = async (profileId, ownerUserId, file, previousPhotoPath) => {
  try {
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
    const path = `profilePictures/${ownerUserId}/${profileId}_${Date.now()}.${ext}`;
    const fileRef = ref(storage, path);

    await withTimeout(
      uploadBytes(fileRef, file, { contentType: file.type }),
      20000,
      "Upload timed out"
    );
    const photoURL = await getDownloadURL(fileRef);

    await updateDoc(doc(db, "profiles", profileId), { photoURL, photoPath: path });

    if (previousPhotoPath) {
      deleteObject(ref(storage, previousPhotoPath)).catch(() => {});
    }

    console.log("Profile Picture Updated: ", profileId);
    return { success: true, photoURL };
  } catch (error) {
    console.error("Transmission Error: ", error);
    return { success: false, error };
  }
};

/**
 * Archive a profile (soft-delete). Never hard-deletes so log history stays
 * associated with the profileId.
 */
export const archiveProfile = async (profileId) => {
  try {
    await updateDoc(doc(db, "profiles", profileId), { isActive: false });
    console.log("Profile Archived: ", profileId);
    return { success: true, id: profileId };
  } catch (error) {
    console.error("Transmission Error: ", error);
    return { success: false, error };
  }
};
